import type { FlexProps } from '@chakra-ui/react';
import { Flex, Icon, Text, useDisclosure } from '@chakra-ui/react';
import type { CardProps } from '@ui/components/Card';
import { Card } from '@ui/components/Card';
import { ExpandMore } from '@veno-app/icons';
import type { PropsWithChildren } from 'react';
import React, { Children } from 'react';

export type GardenBoostCardProps = CardProps & {
  isExpandable?: boolean;
  avatarText: string;
};
export const GardenBoostCard = ({
  isExpandable,
  avatarText,
  ...props
}: PropsWithChildren<GardenBoostCardProps>) => {
  const { isOpen: isExpanded, onToggle: toggleExpanded } = useDisclosure();

  const children = Children.toArray(props.children);

  // chakra image to use in avatar; body of card if not expanded; additional content if expanded
  const [avatarImage, body, more] = children;

  return (
    <Card
      m="0"
      py={{
        base: '8px',
        desktop: '16px',
      }}
      px="16px"
      position="relative"
      isActive={isExpanded}
      cursor={isExpandable ? 'pointer' : 'auto'}
      onClick={isExpandable ? toggleExpanded : undefined}
      {...props}
    >
      {isExpandable && (
        <Icon
          as={ExpandMore}
          boxSize="24px"
          position="absolute"
          top="8px"
          right="16px"
          color="text-link"
          transform={isExpanded ? 'rotate(180deg)' : undefined}
        />
      )}
      <Flex gap={4}>
        <GardenWaterBoostCardAvatar avatarText={avatarText}>
          {avatarImage}
        </GardenWaterBoostCardAvatar>
        {body}
      </Flex>
      {isExpanded && more}
    </Card>
  );
};

const GardenWaterBoostCardAvatar = ({
  avatarText,
  children,
  ...props
}: PropsWithChildren<
  FlexProps & {
    avatarText: string;
  }
>) => {
  return (
    <Flex {...props}>
      <Flex
        overflow="hidden"
        w={{
          base: '50px',
          desktop: '64px',
        }}
        h={{
          base: '50px',
          desktop: '64px',
        }}
        flexShrink="0"
        rounded="full"
        background="linear-gradient(135.52deg, #EBF0F3 14.99%, #D6E0E7 84.37%)"
        shadow="card.active"
        position="relative"
        justify="center"
        alignItems="center"
      >
        {children}
        <Text
          zIndex={0}
          color="brand-black"
          textAlign="center"
          textStyle={
            avatarText.length <= 5
              ? {
                  base: 'bodyBold',
                  desktop: 'h4Bold',
                }
              : {
                  base: 'bodySmallBold',
                  desktop: 'bodyBold',
                }
          }
        >
          {avatarText}
        </Text>
      </Flex>
    </Flex>
  );
};
